import { useEffect, useState } from "react";
import { type 마켓봄피쳐 } from "./데이터/마켓봄피쳐";
import styles from "./유통서비스의_어려움_Desktop.module.css";
import Link from "next/link";
import CheckHover from "../public/assets/pc/images/pic/card_check-hover.svg";
import CheckActive from "../public/assets/pc/images/pic/card_check_active.svg";
import Image from "next/image";
import WaveImage from "/public/assets/pc/images/pic/wave.svg";
import { useTracker } from "./util/Tracker";

type 버튼_Content = {
  content: string;
  emoji: string;
};

export const 유통서비스가_어려운_이유: Partial<
  Record<마켓봄피쳐, 버튼_Content>
> = {
  거래처주문앱: { content: "불편한\n주문방식", emoji: "🥺" },
  자동취합: { content: "수발주 취합의\n번거로움", emoji: "📑" },
  미수금0: { content: "외상 거래로 인한\n 미수금 증가", emoji: "💰" },
  무제한추가: { content: "거래처 추가에 따른\n요금제 인상", emoji: "🔥" },
  거래처별원클릭단가: { content: "거래처별로 다른\n단가 관리", emoji: "🫠" },
  유통사앱즉시확인: { content: "실시간 매출/매입\n조회 불가", emoji: "📊" },
};

const getKey = <K extends string = string>(
  record: Record<K, unknown> | Partial<Record<K, unknown>>
): K[] => Object.keys(record) as K[];

type 어려움_버튼Props = {
  마켓봄피쳐: 마켓봄피쳐;
  onClick: () => void;
  status: boolean;
  idx: number;
  waveTrigger: number;
};

type Wave = "off" | "waveMini" | "wave";
const 어려움_버튼 = ({
  마켓봄피쳐,
  onClick,
  status,
  idx,
  waveTrigger,
}: 어려움_버튼Props) => {
  const [hover, setHover] = useState<boolean>(false);
  const [wave, setWave] = useState<Wave>("wave");
  const animationDelay = 0.05 * (1 + idx * 2) + 0.3;
  const animationDuration = 800; //must be synced with css

  useEffect(() => {
    if (wave !== "wave") setWave("waveMini");
    const removeWave = setTimeout(() => {
      setWave("off");
    }, animationDelay * 1000 + animationDuration);
    return () => {
      clearTimeout(removeWave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveTrigger]);

  return (
    <button
      onClick={() => {
        onClick();
      }}
      className={`${status ? styles["active"] : styles["inactive"]} ${
        styles[마켓봄피쳐]
      } ${styles["button"]} ${마켓봄피쳐} ${styles[wave]}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        animationDelay: wave !== "off" ? `${animationDelay}s` : "0s",
      }}
    >
      <div>
        <Image
          src={status ? CheckActive : CheckHover}
          alt=""
          className={`${
            hover || status || wave === "wave" ? styles["norm"] : styles["flip"]
          } ${styles["coin"]}`}
          style={{ opacity: status ? 1.0 : 0.6, backfaceVisibility: "hidden" }}
        />
        <div
          className={`${
            hover || status || wave === "wave" ? styles["flip"] : styles["norm"]
          } ${styles["coin"]} ${styles["emoji"]}`}
          style={{
            backfaceVisibility: "hidden",
            marginTop: "-130%",
          }}
        >
          {유통서비스가_어려운_이유[마켓봄피쳐]?.emoji}
        </div>
      </div>

      {유통서비스가_어려운_이유[마켓봄피쳐]?.content}
    </button>
  );
};

export const 유통서비스의_어려움_Desktop = () => {
  const [선택한목록, set선택한목록] = useState<마켓봄피쳐[]>([]);
  const [waveInvoker, setWaveInvoker] = useState<number>(1);
  const tracker = useTracker();

  const toggle피쳐 = (피쳐: 마켓봄피쳐) => {
    if (선택한목록.includes(피쳐))
      set선택한목록(선택한목록.filter((x) => x !== 피쳐));
    else {
      set선택한목록([...선택한목록, 피쳐]);
      tracker.trackingClick({
        depth1: "HOME",
        depth2: "문제선택",
        depth3: 피쳐,
      });
    }
  };

  return (
    <div className={styles["유통서비스의어려움"]}>
      <div className={styles["overlay"]}>
        <div
          className={`${styles["curlWave"]}`}
          style={{ background: `url(${WaveImage.src})` }}
        />
        <div
          className={`${styles["curlWave"]} ${styles["curlWave2"]}`}
          style={{ background: `url(${WaveImage.src})`, marginLeft: "-100%" }}
        />
        <div
          className={`${styles["curlWave"]} ${styles["curlWave3"]}`}
          style={{
            background: `url(${WaveImage.src})`,
          }}
        />
      </div>
      <div className={styles["header"]}>
        <div className={styles["mainHeader"]}>
          {"수발주 관리에\n어떤 어려움이 있으신가요?"}
        </div>
        <div className={styles["subHeader"]}>
          {"어렵다고 느끼는 부분을 모두 선택하고 결과를 확인해 보세요."}
        </div>
      </div>
      <div className={styles["어려움목록"]}>
        {getKey<마켓봄피쳐>(유통서비스가_어려운_이유).map(
          (피쳐: 마켓봄피쳐, idx) => (
            <어려움_버튼
              마켓봄피쳐={피쳐}
              onClick={() => {
                toggle피쳐(피쳐);
              }}
              status={선택한목록.includes(피쳐)}
              key={피쳐}
              idx={idx}
              waveTrigger={waveInvoker}
            />
          )
        )}
      </div>
      <div className={styles["bottom"]}>
        <span
          className={`${styles["subBottom"]} ${
            선택한목록.length !== 0 ? styles["subBottom-active"] : ""
          }`}
        >
          {"마켓봄은 이런 문제를 어떻게 해결했을까요?"}
        </span>
        <Link
          onClick={(e) => {
            if (선택한목록.length === 0) {
              setWaveInvoker(waveInvoker * -1);
              e.preventDefault();
            } else {
              tracker.trackingClick({
                depth1: "HOME",
                depth2: "문제결과확인",
              });
            }
          }}
          className={`${styles["proceed"]} ${
            styles[
              선택한목록.length === 0 ? "proceed-inactive" : "proceed-active"
            ]
          }`}
          href={`/features/?featureList=${JSON.stringify(선택한목록)}`}
        >
          {선택한목록.length === 0 ? "결과 확인하기" : "결과 확인하기"}
        </Link>
      </div>
    </div>
  );
};
